import { HOST_QUERY_KEY, metaApiInstance, REGION } from '@kc-console/context';
import { createAxiosInstance, STATUS_CODE } from '@kc-console/core';

import { getRequestUrlData } from '../../../utils/getRequestUrlData';

import type { GetServerSidePropsContext } from 'next';
import type { QueryClient } from '@tanstack/react-query';
import type { Region } from '@kc-console/context';

const OBJECT_STORAGE_KEYWORD = 'object-storage';
const BUCKETS_PATH_PREFIX = '/object-storage/buckets/';
const CLASSIC_BUCKETS_PATH_PREFIX = '/object-storage/classic-buckets/';
const CLASSIC_BUCKET_TYPE = 'hot';

type V1ExtBucketItem = {
  name: string;
  type: string;
};

type V1ExtBucketResponse = {
  count: number;
  items: V1ExtBucketItem[];
  totalCount: number;
};

type CheckClassicBucketGuardParams = {
  context: GetServerSidePropsContext;
  language: string;
  queryClient: QueryClient;
  token: string;
  region?: Region;
};

const PASS = { status: STATUS_CODE.OK } as const;

/**
 * 버킷 상세 진입 시 classic 버킷이면 classic-buckets 경로로 full-reload 리다이렉트하는 가드의 공통 로직.
 * @description
 * - kr-gov-central-1 리전에서만 동작
 * - /object-storage/buckets/{bucketName}/* 경로일 때만 수행
 * - v1_ext/bucket prefix 조회 응답 type === 'hot' 인 경우 classic 으로 판단
 * - 리다이렉트 destination 은 origin 포함 절대 URL 로 구성되어 baguni Next 라우팅에서 벗어나 full reload 진입
 */
export const checkClassicBucketGuard = async ({
  context,
  language,
  queryClient,
  region,
  token,
}: CheckClassicBucketGuardParams) => {
  if (region !== REGION.KR_GOV_CENTRAL_1) return PASS;

  const { urlObj } = getRequestUrlData(context);
  const { pathname, search, origin } = urlObj;

  if (!pathname.startsWith(BUCKETS_PATH_PREFIX)) return PASS;

  const bucketName = pathname.split('/')[3] ?? '';

  if (!bucketName) return PASS;

  try {
    const { fetchMegaMenu, fetchServiceMeta } = metaApiInstance({
      token,
      overrideAxiosRequestConfig: {
        headers: {
          Cookie: `KC_LANG=${language}`,
        },
      },
    });

    const categories = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_MEGA_MENU, region ? { language, region } : { language }],
      queryFn: () => fetchMegaMenu({ region }),
    });

    const matchedService = categories
      .flatMap(({ services }) => services)
      .find(service => service.keyword === OBJECT_STORAGE_KEYWORD);

    if (!matchedService) return PASS;

    const serviceId = matchedService.id;

    const serviceMeta = await queryClient.fetchQuery({
      queryKey: [
        HOST_QUERY_KEY.GET_SERVICE_META,
        region ? { serviceId, language, region } : { serviceId, language },
      ],
      queryFn: () => fetchServiceMeta({ serviceId, region }),
    });

    const objectStorageDefault = serviceMeta?.endpoints?.objectStorageDefault ?? '';

    if (!objectStorageDefault) return PASS;

    const objectStorageApi = createAxiosInstance({ token, baseURL: objectStorageDefault });

    const result = await objectStorageApi.get<unknown, V1ExtBucketResponse>('/v1_ext/bucket', {
      params: { prefix: bucketName },
    });

    const targetBucket = result?.items?.[0];

    if (!targetBucket || targetBucket.name !== bucketName) return PASS;
    if (targetBucket.type !== CLASSIC_BUCKET_TYPE) return PASS;

    const destinationPath = pathname.replace(BUCKETS_PATH_PREFIX, CLASSIC_BUCKETS_PATH_PREFIX);
    return {
      redirect: {
        destination: `${origin}${destinationPath}${search}`,
        permanent: false,
      },
    };
  } catch {
    return PASS;
  }
};

checkClassicBucketGuard.displayName = 'checkClassicBucketGuard';
